import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ShieldCheck, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { usePanel } from "@/context/PanelContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { timeAgo } from "@/lib/utils-tg";

export default function ApprovalsPage() {
  const { openNew } = useOutletContext();
  const { open: openPanel, refreshKey } = usePanel();
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/approvals").then((r) => setList(r.data));
  }, [refreshKey]);

  const pending = list.filter((a) => a.status === "pending");
  const history = list.filter((a) => a.status !== "pending");

  const Row = ({ a }) => (
    <button
      onClick={() => openPanel("approval", a)}
      className="w-full text-left flex items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors first:rounded-t-2xl last:rounded-b-2xl"
      data-testid={`approval-row-${a.id}`}
    >
      <div className="tg-stat-icon bg-orange-50">
        <ShieldCheck className="h-5 w-5 text-orange-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 truncate">{a.title}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {a.type}{a.amount != null ? ` · $${Number(a.amount).toFixed(2)}` : ""} · {timeAgo(a.created_at)}
        </div>
      </div>
      <span
        className={`tg-pill ${
          a.status === "approved"
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : a.status === "rejected"
            ? "bg-rose-50 text-rose-700 border-rose-200"
            : "bg-orange-50 text-orange-700 border-orange-200"
        }`}
      >
        {a.status}
      </span>
    </button>
  );

  return (
    <div className="px-6 md:px-10 pt-6 max-w-5xl mx-auto" data-testid="approvals-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Approvals</h1>
          <p className="text-slate-500 mt-1">Decisions and validations — kept simple.</p>
        </div>
        <button onClick={openNew} className="tg-btn-primary" data-testid="approvals-new">
          <Plus className="h-4 w-4" /> Request
        </button>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="pending" className="rounded-lg" data-testid="tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg" data-testid="tab-history">
            History ({history.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <div className="tg-card divide-y divide-slate-100">
            {pending.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">No pending approvals.</div>
            )}
            {pending.map((a) => <Row key={a.id} a={a} />)}
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="tg-card divide-y divide-slate-100">
            {history.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm">No history yet.</div>
            )}
            {history.map((a) => <Row key={a.id} a={a} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
